import { useSortable } from '@dnd-kit/sortable';
import { components, OptionProps } from 'react-select';
import { useDispatch } from 'react-redux';
import { setEditingMap } from 'store/slices/CustomMapSlice';
import React from 'react';
import { OptionType } from 'components/CustomMapSelect';
import { CustomOptionDiv, CustomSelectDiv, LabelDiv, UploadLabel, UploadText } from '../style';
import CustomTooltip from 'components/Basic/Tooltip';
import { CSS } from '@dnd-kit/utilities';

type CustomOptionProps = OptionProps<OptionType, true> & {
	items: OptionType[];
	isEditDisabled: boolean;
};
const MAX_FILE_UPLOAD_LIMIT = 10;
export default function CustomOption(props: CustomOptionProps) {
	const {isEditDisabled, data, items, isSelected, isDisabled, innerProps } = props;
	const dispatch = useDispatch();
	const { setNodeRef, isDragging, attributes, listeners, transform, transition } = useSortable({
		id: data.value,
		disabled: isDisabled,
	});

	if (data.value === -1) {
		const hasReachedMaxLimit = items?.length >= MAX_FILE_UPLOAD_LIMIT;
		const disableAddFile = hasReachedMaxLimit || isDisabled || isEditDisabled;
		return (
			<components.Option {...props}>
				<UploadLabel $disabled={disableAddFile}
					htmlFor={disableAddFile ? undefined : 'file-uploader'} 
					title={hasReachedMaxLimit ? 'Maximum 10 files allowed' : undefined}
					onPointerDown={(e) => {
						if(disableAddFile) return;
						e.stopPropagation();
						// fileLabelRef.current?.click();
					}}
				>
					<UploadText>{data.label}</UploadText>
					<span style={{display: 'flex', alignItems: 'center'}}>
						<svg
							xmlns="http://www.w3.org/2000/svg" width="21" height="21" viewBox="0 0 27 27" fill="none">
							<path d="M26.2051 13.4209C26.2051 20.4803 20.4823 26.2031 13.4229 26.2031C6.36342 26.2031 
							0.640625 20.4803 0.640625 13.4209C0.640625 6.36147 6.36342 0.638672 13.4229 0.638672C20.4823 
							0.638672 26.2051 6.36147 26.2051 13.4209Z" fill="var(--defaultBgColor)" stroke="var(--defaultIconColor)" 
							strokeWidth="1.27822" strokeLinecap="round" strokeLinejoin="round"/>
							<path d="M13.4248 9.16016V17.6816M17.6855 13.4209H9.16406" stroke="var(--defaultIconColor)" 
								strokeWidth="1.91" strokeLinecap="round" strokeLinejoin="round"/>
						</svg>
					</span>
				</UploadLabel>
			</components.Option>
		);
	}

	const style = {
		transform: CSS.Transform.toString(transform),
		transition,
	};

	function handleEditMap(e: React.MouseEvent<SVGElement>) {
		e.preventDefault();
		e.stopPropagation();

		if (!props.isSelected) {
			props.selectOption(data);
		}
		dispatch(setEditingMap(data.value));
	}

	return (
		<components.Option {...props}>
			<CustomSelectDiv
				{...innerProps}
				ref={setNodeRef}
				style={{
					...style,
					background: isDragging ? 'var(--selectDropdownHoverColor)' : 'transparent',
					cursor: isDisabled ? 'not-allowed' : 'default',
					color: isDisabled ? 'var(--secondaryTextColor)' : 'var(--textHeadingColor)',
				}}
				{...attributes}
			>
				<LabelDiv>
					<span 
						style={{
							touchAction: 'none'
						}}
						className='drag-area'
						{...listeners}>
					</span>
					<span
						style={{ 
							touchAction: 'none', 
							cursor: isDisabled ? 'not-allowed' : isDragging ? 'grabbing' : 'grab',
							display: 'flex', 
							position: 'relative', 
							opacity: '.75'
						}}>
						<svg xmlns="http://www.w3.org/2000/svg" width="7.5" height="16" viewBox="0 0 9 19" fill="none">
							<path d="M0.5 17.5C0.5 18.0523 0.947715 18.5 1.5 18.5C2.05228 18.5 2.5 18.0523 2.5 17.5C2.5 16.9477 
							2.05228 16.5 1.5 16.5C0.947715 16.5 0.5 16.9477 0.5 17.5Z" fill="var(--textColor)" stroke="var(--textColor)"/>
							<path d="M0.5 9.5C0.5 10.0523 0.947715 10.5 1.5 10.5C2.05228 10.5 2.5 10.0523 2.5 9.5C2.5 8.94772 
							2.05228 8.5 1.5 8.5C0.947715 8.5 0.5 8.94772 0.5 9.5Z" fill="var(--textColor)" stroke="var(--textColor)"/>
							<path d="M0.5 1.5C0.5 2.05228 0.947715 2.5 1.5 2.5C2.05228 2.5 2.5 2.05228 2.5 1.5C2.5 0.947715 2.05228 
							0.5 1.5 0.5C0.947715 0.5 0.5 0.947715 0.5 1.5Z" fill="var(--textColor)" stroke="var(--textColor)"/>
							<path d="M6.5 17.5C6.5 18.0523 6.94772 18.5 7.5 18.5C8.05228 18.5 8.5 18.0523 8.5 17.5C8.5 16.9477 8.05228 
							16.5 7.5 16.5C6.94772 16.5 6.5 16.9477 6.5 17.5Z" fill="var(--textColor)" stroke="var(--textColor)"/>
							<path d="M6.5 9.5C6.5 10.0523 6.94772 10.5 7.5 10.5C8.05228 10.5 8.5 10.0523 8.5 9.5C8.5 8.94772 8.05228 
							8.5 7.5 8.5C6.94772 8.5 6.5 8.94772 6.5 9.5Z" fill="var(--textColor)" stroke="var(--textColor)"/>
							<path d="M6.5 1.5C6.5 2.05228 6.94772 2.5 7.5 2.5C8.05228 2.5 8.5 2.05228 8.5 1.5C8.5 0.947715 
							8.05228 0.5 7.5 0.5C6.94772 0.5 6.5 0.947715 6.5 1.5Z" fill="var(--textColor)" stroke="var(--textColor)"/>
						</svg>
					</span>
					{ isSelected ?
						<span style={{
							cursor: isDisabled ? 'not-allowed' : 'pointer',
							display: 'flex', 
							position:'relative'
						}}>
							<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 26 26" fill="none">
								<rect x="0.835938" y="0.837891" width="23.4668" height="23.4668" rx="11.7334" fill="var(--defaultIconColor)"/>
								<rect x="0.835938" y="0.837891" width="23.4668" height="23.4668" rx="11.7334" fill="var(--primary)"/>
								<rect x="0.835938" y="0.837891" width="23.4668" height="23.4668" rx="11.7334" fill="var(--defaultBgLightColor)"/>
								<rect x="0.835938" y="0.837891" width="23.4668" height="23.4668" rx="11.7334" stroke="var(--primary)" 
									strokeWidth="1.6762"/>
								<path d="M6.79688 14.1205L9.89672 16.784C10.1058 16.9567 10.4149 16.9385 10.5967 16.7385L18.3417 
								8.24805" stroke="var(--primary)" strokeWidth="1.6762" strokeMiterlimit="10" strokeLinecap="round"/>
							</svg>
						</span>
						:
						<span style={{
							cursor: isDisabled ? 'not-allowed' : 'pointer',
							display: 'flex', 
							position:'relative'
						}}>
							<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 26 26" fill="none">
								<rect x="0.835938" y="0.837891" width="23.4668" height="23.4668" rx="11.7334" fill="var(--defaultBgColor)"/>
								<rect x="0.835938" y="0.837891" width="23.4668" height="23.4668" rx="11.7334" fill="var(--inputHoverBorderColor)"/>
								<rect x="0.835938" y="0.837891" width="23.4668" height="23.4668" rx="11.7334" fill="var(--defaultBgColor)"/>
								<rect x="0.835938" y="0.837891" width="23.4668" height="23.4668" rx="11.7334" stroke="var(--inputHoverBorderColor)" 
									strokeWidth="1.6762"/>
							</svg>
						</span>
					} 
					<label 
						title={data.label} 
						className='custom-map-option-label'>
						{data.label}
					</label>
				</LabelDiv>
				<CustomOptionDiv
					onPointerDown={(e) => {
						e.preventDefault();
						e.stopPropagation();
					}}
					onClick={(e) => {
						e.preventDefault();
						e.stopPropagation();
					}}>
					
					<span id="edit-map" style={{ cursor: (isDisabled || isEditDisabled) ? 'not-allowed' : 'pointer' }}>
						<svg 
							onPointerDown={(e) => {
								if(isEditDisabled) return;
								e.preventDefault();
								e.stopPropagation();
							}}
							onClick={handleEditMap}
							xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 27 27" fill="none">
							<path d="M1 25.459H25.459M13.5312 4.84278L17.3739 1L24.1002 7.72622L20.2574 11.569M13.5312 
							4.84278L5.91218 12.4618C5.65733 12.7165 5.51412 13.0621 5.51404 13.4224V19.5861H11.6777C12.0381 19.586 
							12.3836 19.4428 12.6384 19.188L20.2574 11.569M13.5312 4.84278L20.2574 11.569" 
							stroke={(isDisabled || isEditDisabled) ? 'rgba(58, 68, 89, 0.3)' : '#9F9F9F'} 
							strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
						</svg>
					</span>
					<CustomTooltip elementID="edit-map" content="Edit Map" place="right" />

				</CustomOptionDiv>
			</CustomSelectDiv>
		</components.Option>
	);
}
